/**
 * Water Leak Sensor Calculations Utility
 *
 * Provides comprehensive water leak detection and flood monitoring functions for the IoT Edge Platform.
 * Supports water presence detection, leak severity assessment, water depth estimation,
 * damage risk evaluation, and emergency response recommendations.
 *
 * Features:
 * - Water detection status (4 levels: dry, moisture, wet, flooded)
 * - Leak severity classification (5 levels)
 * - Water depth estimation (mm/cm)
 * - Damage risk assessment (5 risk levels)
 * - Response time recommendations
 * - Statistical analysis (leak events, total water volume, response times)
 * - Context-aware safety recommendations
 * - Emergency action generation
 * - Bilingual support (Arabic/English)
 *
 * @module waterLeakCalculations
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Water detection status
 */
export type WaterStatus = 'dry' | 'moisture' | 'wet' | 'flooded';

/**
 * Leak severity level
 */
export type LeakSeverity = 'none' | 'minor' | 'moderate' | 'major' | 'critical';

/**
 * Damage risk level
 */
export type DamageRisk = 'none' | 'low' | 'moderate' | 'high' | 'severe';

/**
 * Water leak reading data point
 */
export interface WaterLeakReading {
  waterDetected: boolean;
  waterLevel?: number; // Water level/depth in mm
  moisture?: number; // Moisture percentage (0-100%)
  time: Date;
  temperature?: number; // Optional ambient temperature (°C)
}

/**
 * Water detection thresholds
 */
export interface WaterThresholds {
  dry: number; // <5% moisture
  moisture: number; // 5-30% moisture
  wet: number; // 30-70% moisture
  flooded: number; // >70% moisture
  minorDepth: number; // <5mm water
  moderateDepth: number; // 5-20mm
  majorDepth: number; // 20-50mm
  criticalDepth: number; // >50mm
}

/**
 * Leak event information
 */
export interface LeakEvent {
  severity: LeakSeverity;
  startTime: Date;
  duration: number; // Duration in seconds
  peakWaterLevel: number; // Highest water level in mm
  estimatedVolume: number; // Estimated water volume in liters
  responseTime?: number; // Time to respond in seconds
  resolved: boolean;
}

/**
 * Damage risk assessment
 */
export interface DamageRiskInfo {
  level: DamageRisk;
  affectedAreas: string[]; // Areas at risk (floor, walls, equipment, etc.)
  estimatedCost: number; // Estimated damage cost (SAR)
  urgency: 'low' | 'medium' | 'high' | 'immediate'; // Response urgency
  preventable: boolean; // Whether damage is still preventable
}

/**
 * Water leak statistics over a time period
 */
export interface WaterLeakStats {
  totalLeaks: number;
  minorLeaks: number;
  moderateLeaks: number;
  majorLeaks: number;
  criticalLeaks: number;
  totalWaterDetected: number; // Total water volume in liters
  averageResponseTime: number; // Average response time in seconds
  longestLeak: number; // Longest leak duration in seconds
  dryHours: number; // Hours with no water detected
  wetHours: number; // Hours with water present
  averageRecoveryTime: number; // Average time to resolve leak
}

/**
 * Comprehensive water leak sensor status
 */
export interface WaterLeakStatus {
  waterStatus: WaterStatus;
  waterLevel: number; // mm
  moistureLevel: number; // percentage
  leakSeverity: LeakSeverity;
  damageRisk: DamageRiskInfo;
  currentLeak?: LeakEvent;
  recommendations: string[];
  emergencyActions: string[];
  stats: WaterLeakStats;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Standard water detection thresholds
 */
export const WATER_THRESHOLDS: WaterThresholds = {
  dry: 5,
  moisture: 30,
  wet: 70,
  flooded: 100,
  minorDepth: 5,
  moderateDepth: 20,
  majorDepth: 50,
  criticalDepth: 100,
};

/**
 * Minimum leak duration to register as event (seconds)
 */
export const MIN_LEAK_DURATION = 5;

/**
 * Area size for volume estimation (square meters)
 */
export const DEFAULT_AREA_SIZE = 10; // 10 m²

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Get water detection status based on moisture and water level
 */
export function getWaterStatus(moisture: number, waterLevel: number): WaterStatus {
  // Prioritize water level if detected
  if (waterLevel > 0) {
    if (waterLevel >= WATER_THRESHOLDS.majorDepth) return 'flooded';
    if (waterLevel >= WATER_THRESHOLDS.moderateDepth) return 'wet';
    return 'moisture';
  }

  // Fall back to moisture level
  if (moisture < WATER_THRESHOLDS.dry) return 'dry';
  if (moisture < WATER_THRESHOLDS.moisture) return 'moisture';
  if (moisture < WATER_THRESHOLDS.wet) return 'wet';
  return 'flooded';
}

/**
 * Assess leak severity based on water level and duration
 */
export function assessLeakSeverity(
  waterLevel: number,
  duration: number,
  readings: WaterLeakReading[],
): LeakSeverity {
  if (waterLevel === 0) return 'none';

  // Check for rapid increase (indicates burst pipe or major leak)
  const rapidIncrease = detectRapidIncrease(readings);

  // Base severity on water depth
  let severity: LeakSeverity = 'minor';

  if (waterLevel < WATER_THRESHOLDS.minorDepth) {
    severity = 'minor';
  } else if (waterLevel < WATER_THRESHOLDS.moderateDepth) {
    severity = 'moderate';
  } else if (waterLevel < WATER_THRESHOLDS.majorDepth) {
    severity = 'major';
  } else {
    severity = 'critical';
  }

  // Escalate severity if rapid increase or long duration
  if (rapidIncrease) {
    if (severity === 'minor') severity = 'moderate';
    else if (severity === 'moderate') severity = 'major';
    else if (severity === 'major') severity = 'critical';
  }

  // Escalate if leak has been ongoing for a long time
  if (duration > 3600) {
    // >1 hour
    if (severity === 'minor') severity = 'moderate';
    else if (severity === 'moderate') severity = 'major';
  }

  return severity;
}

/**
 * Detect rapid water level increase
 */
function detectRapidIncrease(readings: WaterLeakReading[]): boolean {
  if (readings.length < 3) return false;

  // Get readings from last 5 minutes
  const now = new Date();
  const recentReadings = readings
    .filter((r) => now.getTime() - r.time.getTime() < 5 * 60 * 1000)
    .sort((a, b) => a.time.getTime() - b.time.getTime());

  if (recentReadings.length < 2) return false;

  // Check if water level increased by >10mm in 5 minutes
  const firstReading = recentReadings[0];
  const lastReading = recentReadings[recentReadings.length - 1];

  const levelIncrease = (lastReading.waterLevel || 0) - (firstReading.waterLevel || 0);

  return levelIncrease > 10; // >10mm increase indicates rapid leak
}

/**
 * Estimate water volume from water level and area
 */
export function estimateWaterVolume(
  waterLevel: number,
  areaSize: number = DEFAULT_AREA_SIZE,
): number {
  // Volume (liters) = area (m²) × depth (m) × 1000 (L/m³)
  const depthInMeters = waterLevel / 1000;
  const volumeLiters = areaSize * depthInMeters * 1000;
  return Math.round(volumeLiters * 10) / 10; // Round to 1 decimal
}

/**
 * Assess damage risk based on leak severity and duration
 */
export function assessDamageRisk(
  severity: LeakSeverity,
  duration: number,
  waterLevel: number,
): DamageRiskInfo {
  let level: DamageRisk = 'none';
  let affectedAreas: string[] = [];
  let estimatedCost = 0;
  let urgency: DamageRiskInfo['urgency'] = 'low';
  let preventable = true;

  if (severity === 'none') {
    return { level, affectedAreas, estimatedCost, urgency, preventable };
  }

  // Determine risk level
  if (severity === 'minor' && duration < 600) {
    level = 'low';
    affectedAreas = ['floor surface'];
    estimatedCost = 500;
    urgency = 'medium';
  } else if (severity === 'moderate' || (severity === 'minor' && duration >= 600)) {
    level = 'moderate';
    affectedAreas = ['floor', 'baseboards'];
    estimatedCost = 2000;
    urgency = 'high';
  } else if (severity === 'major' || duration > 1800) {
    level = 'high';
    affectedAreas = ['floor', 'walls', 'furniture'];
    estimatedCost = 5000;
    urgency = 'immediate';
    preventable = duration < 3600; // Still preventable if <1 hour
  } else if (severity === 'critical') {
    level = 'severe';
    affectedAreas = ['floor', 'walls', 'furniture', 'electrical equipment', 'structure'];
    estimatedCost = 15000;
    urgency = 'immediate';
    preventable = false;
  }

  // Escalate if water level is very high
  if (waterLevel > 50) {
    if (level === 'low') level = 'moderate';
    else if (level === 'moderate') level = 'high';
    else if (level === 'high') level = 'severe';
    preventable = false;
  }

  return {
    level,
    affectedAreas,
    estimatedCost,
    urgency,
    preventable,
  };
}

/**
 * Detect and track leak events
 */
export function detectLeakEvent(
  readings: WaterLeakReading[],
  currentWaterLevel: number,
  currentMoisture: number,
): LeakEvent | undefined {
  if (currentWaterLevel === 0 && currentMoisture < WATER_THRESHOLDS.moisture) {
    return undefined; // No leak
  }

  // Find when leak started
  const now = new Date();
  const sortedReadings = [...readings].sort((a, b) => a.time.getTime() - b.time.getTime());

  let startTime: Date | undefined;
  let peakWaterLevel = currentWaterLevel;

  // Find leak start time (first water detection)
  for (let i = sortedReadings.length - 1; i >= 0; i--) {
    const reading = sortedReadings[i];
    if (
      reading.waterDetected ||
      (reading.moisture && reading.moisture >= WATER_THRESHOLDS.moisture)
    ) {
      startTime = reading.time;
      if (reading.waterLevel && reading.waterLevel > peakWaterLevel) {
        peakWaterLevel = reading.waterLevel;
      }
    } else {
      break; // Found the start of leak
    }
  }

  if (!startTime) {
    startTime = now; // Leak just started
  }

  const duration = Math.floor((now.getTime() - startTime.getTime()) / 1000);

  // Skip if duration too short
  if (duration < MIN_LEAK_DURATION) {
    return undefined;
  }

  const severity = assessLeakSeverity(peakWaterLevel, duration, readings);
  const estimatedVolume = estimateWaterVolume(peakWaterLevel);

  return {
    severity,
    startTime,
    duration,
    peakWaterLevel,
    estimatedVolume,
    resolved: false,
  };
}

/**
 * Calculate water leak statistics for a time period
 */
export function getWaterLeakStatistics(
  readings: WaterLeakReading[],
  startTime: Date,
  endTime: Date,
): WaterLeakStats {
  const periodReadings = readings
    .filter((r) => r.time >= startTime && r.time <= endTime)
    .sort((a, b) => a.time.getTime() - b.time.getTime());

  if (periodReadings.length === 0) {
    return {
      totalLeaks: 0,
      minorLeaks: 0,
      moderateLeaks: 0,
      majorLeaks: 0,
      criticalLeaks: 0,
      totalWaterDetected: 0,
      averageResponseTime: 0,
      longestLeak: 0,
      dryHours: 0,
      wetHours: 0,
      averageRecoveryTime: 0,
    };
  }

  // Track leak events
  const leakEvents: Array<{ severity: LeakSeverity; duration: number; volume: number }> = [];
  let currentLeakStart: Date | undefined;
  let currentPeakLevel = 0;

  periodReadings.forEach((reading, index) => {
    const hasWater =
      reading.waterDetected || (reading.moisture && reading.moisture >= WATER_THRESHOLDS.moisture);

    if (hasWater && !currentLeakStart) {
      // Leak started
      currentLeakStart = reading.time;
      currentPeakLevel = reading.waterLevel || 0;
    } else if (hasWater && currentLeakStart) {
      // Leak continuing
      currentPeakLevel = Math.max(currentPeakLevel, reading.waterLevel || 0);
    } else if (!hasWater && currentLeakStart) {
      // Leak ended
      const duration = Math.floor((reading.time.getTime() - currentLeakStart.getTime()) / 1000);
      if (duration >= MIN_LEAK_DURATION) {
        const severity = assessLeakSeverity(
          currentPeakLevel,
          duration,
          periodReadings.slice(0, index),
        );
        const volume = estimateWaterVolume(currentPeakLevel);
        leakEvents.push({ severity, duration, volume });
      }
      currentLeakStart = undefined;
      currentPeakLevel = 0;
    }
  });

  // Count leaks by severity
  let minorLeaks = 0,
    moderateLeaks = 0,
    majorLeaks = 0,
    criticalLeaks = 0;
  let totalWaterDetected = 0;
  let longestLeak = 0;

  leakEvents.forEach((event) => {
    switch (event.severity) {
      case 'minor':
        minorLeaks++;
        break;
      case 'moderate':
        moderateLeaks++;
        break;
      case 'major':
        majorLeaks++;
        break;
      case 'critical':
        criticalLeaks++;
        break;
    }
    totalWaterDetected += event.volume;
    longestLeak = Math.max(longestLeak, event.duration);
  });

  // Calculate dry vs wet hours
  const totalHours = (endTime.getTime() - startTime.getTime()) / (60 * 60 * 1000);
  const hourlyStatus = new Map<number, boolean>();

  periodReadings.forEach((r) => {
    const hour = Math.floor(r.time.getTime() / (60 * 60 * 1000));
    const hasWater = r.waterDetected || (r.moisture && r.moisture >= WATER_THRESHOLDS.moisture);
    if (hasWater) {
      hourlyStatus.set(hour, true);
    } else if (!hourlyStatus.has(hour)) {
      hourlyStatus.set(hour, false);
    }
  });

  let dryHours = 0,
    wetHours = 0;
  hourlyStatus.forEach((isWet) => {
    if (isWet) wetHours++;
    else dryHours++;
  });

  const averageResponseTime = leakEvents.length > 0 ? 300 : 0; // Mock: 5 minutes
  const averageRecoveryTime =
    leakEvents.length > 0
      ? Math.round(leakEvents.reduce((sum, e) => sum + e.duration, 0) / leakEvents.length)
      : 0;

  return {
    totalLeaks: leakEvents.length,
    minorLeaks,
    moderateLeaks,
    majorLeaks,
    criticalLeaks,
    totalWaterDetected: Math.round(totalWaterDetected * 10) / 10,
    averageResponseTime,
    longestLeak,
    dryHours: Math.round(dryHours * 10) / 10,
    wetHours: Math.round(wetHours * 10) / 10,
    averageRecoveryTime,
  };
}

/**
 * Generate context-aware safety recommendations
 */
export function generateRecommendations(
  waterStatus: WaterStatus,
  severity: LeakSeverity,
  damageRisk: DamageRiskInfo,
  currentLeak?: LeakEvent,
  lang: 'en' | 'ar' = 'en',
): string[] {
  const recommendations: string[] = [];

  if (lang === 'ar') {
    // Arabic recommendations
    if (waterStatus === 'dry') {
      recommendations.push('لا يوجد ماء مكتشف - النظام يعمل بشكل طبيعي');
      recommendations.push('تحقق من المستشعر بشكل دوري للتأكد من عمله');
    } else if (waterStatus === 'moisture') {
      recommendations.push('رطوبة مكتشفة - تحقق من المصدر');
      recommendations.push('قد يكون تكثف طبيعي أو بداية تسرب');
      if (severity === 'minor') {
        recommendations.push('راقب الوضع - لا حاجة لإجراءات فورية');
      }
    } else if (waterStatus === 'wet') {
      recommendations.push('ماء مكتشف - حدد مصدر التسرب فوراً');
      recommendations.push('أوقف مصدر المياه إذا أمكن');
      if (damageRisk.urgency === 'high' || damageRisk.urgency === 'immediate') {
        recommendations.push('اتصل بفني السباكة فوراً');
      }
    } else if (waterStatus === 'flooded') {
      recommendations.push('فيضان مكتشف - إجراءات طارئة مطلوبة');
      recommendations.push('أوقف مصدر المياه الرئيسي فوراً');
      recommendations.push('افصل الكهرباء عن المنطقة المتضررة');
      recommendations.push('ابدأ بإزالة الماء على الفور');
    }

    // Damage prevention
    if (damageRisk.preventable && damageRisk.level !== 'none') {
      recommendations.push('الأضرار لا تزال قابلة للمنع - تصرف بسرعة');
    }

    // Long duration warning
    if (currentLeak && currentLeak.duration > 1800) {
      recommendations.push('تسرب مستمر منذ فترة طويلة - خطر أضرار هيكلية');
    }
  } else {
    // English recommendations
    if (waterStatus === 'dry') {
      recommendations.push('No water detected - system operating normally');
      recommendations.push('Check sensor periodically to ensure proper function');
    } else if (waterStatus === 'moisture') {
      recommendations.push('Moisture detected - investigate source');
      recommendations.push('May be natural condensation or leak beginning');
      if (severity === 'minor') {
        recommendations.push('Monitor situation - no immediate action needed');
      }
    } else if (waterStatus === 'wet') {
      recommendations.push('Water detected - locate leak source immediately');
      recommendations.push('Shut off water supply if possible');
      if (damageRisk.urgency === 'high' || damageRisk.urgency === 'immediate') {
        recommendations.push('Call plumber immediately');
      }
    } else if (waterStatus === 'flooded') {
      recommendations.push('Flooding detected - emergency action required');
      recommendations.push('Shut off main water supply immediately');
      recommendations.push('Disconnect power to affected area');
      recommendations.push('Begin water removal immediately');
    }

    // Damage prevention
    if (damageRisk.preventable && damageRisk.level !== 'none') {
      recommendations.push('Damage still preventable - act quickly');
    }

    // Long duration warning
    if (currentLeak && currentLeak.duration > 1800) {
      recommendations.push('Leak ongoing for extended period - structural damage risk');
    }
  }

  return recommendations.slice(0, 3); // Return top 3 recommendations
}

/**
 * Generate emergency action steps for critical situations
 */
export function generateEmergencyActions(
  waterStatus: WaterStatus,
  severity: LeakSeverity,
  damageRisk: DamageRiskInfo,
  lang: 'en' | 'ar' = 'en',
): string[] {
  // Only generate emergency actions for serious leaks
  if (severity !== 'major' && severity !== 'critical' && waterStatus !== 'flooded') {
    return [];
  }

  const actions: string[] = [];

  if (lang === 'ar') {
    actions.push('أوقف مصدر المياه الرئيسي فوراً');
    actions.push('افصل الكهرباء عن المنطقة المتضررة');

    if (waterStatus === 'flooded') {
      actions.push('انقل الأثاث والمعدات القيمة إلى منطقة جافة');
      actions.push('استخدم مضخة أو مكنسة مياه لإزالة الماء');
    }

    actions.push('اتصل بفني السباكة المتخصص (920003344)');
    actions.push('التقط صوراً للأضرار لأغراض التأمين');

    if (damageRisk.affectedAreas.includes('electrical equipment')) {
      actions.push('لا تلمس أي معدات كهربائية حتى يجف المكان تماماً');
    }

    actions.push('جفف المنطقة بأسرع وقت ممكن (خلال 24-48 ساعة)');
    actions.push('استخدم مراوح ومزيلات الرطوبة لتسريع التجفيف');
  } else {
    actions.push('Shut off main water supply immediately');
    actions.push('Disconnect power to affected area');

    if (waterStatus === 'flooded') {
      actions.push('Move furniture and valuables to dry area');
      actions.push('Use pump or wet vacuum to remove water');
    }

    actions.push('Call emergency plumber (920003344)');
    actions.push('Take photos of damage for insurance purposes');

    if (damageRisk.affectedAreas.includes('electrical equipment')) {
      actions.push('Do not touch electrical equipment until completely dry');
    }

    actions.push('Dry area as quickly as possible (within 24-48 hours)');
    actions.push('Use fans and dehumidifiers to accelerate drying');
  }

  return actions;
}

/**
 * Get comprehensive water leak sensor status
 */
export function getWaterLeakStatus(
  readings: WaterLeakReading[],
  currentWaterLevel: number,
  currentMoisture: number,
  lang: 'en' | 'ar' = 'en',
): WaterLeakStatus {
  const waterStatus = getWaterStatus(currentMoisture, currentWaterLevel);
  const currentLeak = detectLeakEvent(readings, currentWaterLevel, currentMoisture);
  const leakSeverity = currentLeak ? currentLeak.severity : 'none';
  const duration = currentLeak ? currentLeak.duration : 0;
  const damageRisk = assessDamageRisk(leakSeverity, duration, currentWaterLevel);

  // Get yesterday's stats for comparison
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const yesterdayEnd = new Date(yesterday);
  yesterdayEnd.setHours(23, 59, 59, 999);

  const stats = getWaterLeakStatistics(readings, yesterday, yesterdayEnd);

  const recommendations = generateRecommendations(
    waterStatus,
    leakSeverity,
    damageRisk,
    currentLeak,
    lang,
  );

  const emergencyActions = generateEmergencyActions(waterStatus, leakSeverity, damageRisk, lang);

  return {
    waterStatus,
    waterLevel: currentWaterLevel,
    moistureLevel: currentMoisture,
    leakSeverity,
    damageRisk,
    currentLeak,
    recommendations,
    emergencyActions,
    stats,
  };
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format water level with unit
 */
export function formatWaterLevel(level: number, lang: 'en' | 'ar' = 'en'): string {
  if (level === 0) {
    return lang === 'ar' ? 'لا يوجد' : 'None';
  }
  if (level < 10) {
    return lang === 'ar' ? `${level} ملم` : `${level}mm`;
  }
  const cm = (level / 10).toFixed(1);
  return lang === 'ar' ? `${cm} سم` : `${cm}cm`;
}

/**
 * Format moisture percentage
 */
export function formatMoisture(moisture: number, lang: 'en' | 'ar' = 'en'): string {
  const formatted = moisture.toFixed(1);
  return lang === 'ar' ? `${formatted}٪` : `${formatted}%`;
}

/**
 * Format water status
 */
export function formatWaterStatus(status: WaterStatus, lang: 'en' | 'ar' = 'en'): string {
  const labels: Record<WaterStatus, { en: string; ar: string }> = {
    dry: { en: 'Dry', ar: 'جاف' },
    moisture: { en: 'Moisture', ar: 'رطوبة' },
    wet: { en: 'Wet', ar: 'مبلل' },
    flooded: { en: 'Flooded', ar: 'غمر' },
  };
  return labels[status][lang];
}

/**
 * Format leak severity
 */
export function formatLeakSeverity(severity: LeakSeverity, lang: 'en' | 'ar' = 'en'): string {
  const labels: Record<LeakSeverity, { en: string; ar: string }> = {
    none: { en: 'None', ar: 'لا يوجد' },
    minor: { en: 'Minor', ar: 'بسيط' },
    moderate: { en: 'Moderate', ar: 'متوسط' },
    major: { en: 'Major', ar: 'كبير' },
    critical: { en: 'Critical', ar: 'حرج' },
  };
  return labels[severity][lang];
}

/**
 * Format damage risk level
 */
export function formatDamageRisk(risk: DamageRisk, lang: 'en' | 'ar' = 'en'): string {
  const labels: Record<DamageRisk, { en: string; ar: string }> = {
    none: { en: 'None', ar: 'لا يوجد' },
    low: { en: 'Low', ar: 'منخفض' },
    moderate: { en: 'Moderate', ar: 'متوسط' },
    high: { en: 'High', ar: 'مرتفع' },
    severe: { en: 'Severe', ar: 'شديد' },
  };
  return labels[risk][lang];
}

/**
 * Format urgency level
 */
export function formatUrgency(
  urgency: DamageRiskInfo['urgency'],
  lang: 'en' | 'ar' = 'en',
): string {
  const labels: Record<DamageRiskInfo['urgency'], { en: string; ar: string }> = {
    low: { en: 'Low', ar: 'منخفضة' },
    medium: { en: 'Medium', ar: 'متوسطة' },
    high: { en: 'High', ar: 'عالية' },
    immediate: { en: 'Immediate', ar: 'فورية' },
  };
  return labels[urgency][lang];
}

/**
 * Format water volume
 */
export function formatVolume(volume: number, lang: 'en' | 'ar' = 'en'): string {
  if (volume < 1) {
    const ml = Math.round(volume * 1000);
    return lang === 'ar' ? `${ml} مل` : `${ml}ml`;
  }
  return lang === 'ar' ? `${volume.toFixed(1)} لتر` : `${volume.toFixed(1)}L`;
}

/**
 * Format duration in human-readable format
 */
export function formatDuration(seconds: number, lang: 'en' | 'ar' = 'en'): string {
  if (seconds < 60) {
    return lang === 'ar' ? `${seconds} ثانية` : `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    if (remainingSeconds === 0) {
      return lang === 'ar' ? `${minutes} دقيقة` : `${minutes}m`;
    }
    return lang === 'ar'
      ? `${minutes} د ${remainingSeconds} ث`
      : `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return lang === 'ar' ? `${hours} ساعة` : `${hours}h`;
  }
  return lang === 'ar' ? `${hours} س ${remainingMinutes} د` : `${hours}h ${remainingMinutes}m`;
}

/**
 * Format cost in SAR
 */
export function formatCost(cost: number, lang: 'en' | 'ar' = 'en'): string {
  const formatted = cost.toLocaleString();
  return lang === 'ar' ? `${formatted} ريال` : `${formatted} SAR`;
}

// ============================================================================
// Color Functions
// ============================================================================

/**
 * Get color for water status
 */
export function getWaterStatusColor(status: WaterStatus): string {
  const colors: Record<WaterStatus, string> = {
    dry: '#10b981', // green
    moisture: '#eab308', // yellow
    wet: '#f59e0b', // orange
    flooded: '#ef4444', // red
  };
  return colors[status];
}

/**
 * Get color for leak severity
 */
export function getLeakSeverityColor(severity: LeakSeverity): string {
  const colors: Record<LeakSeverity, string> = {
    none: '#10b981', // green
    minor: '#84cc16', // lime
    moderate: '#f59e0b', // orange
    major: '#ef4444', // red
    critical: '#7f1d1d', // dark red
  };
  return colors[severity];
}

/**
 * Get color for damage risk
 */
export function getDamageRiskColor(risk: DamageRisk): string {
  const colors: Record<DamageRisk, string> = {
    none: '#10b981', // green
    low: '#84cc16', // lime
    moderate: '#f59e0b', // orange
    high: '#ef4444', // red
    severe: '#7f1d1d', // dark red
  };
  return colors[risk];
}
